import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { desktopSizes, mobileSizes } from "../util/sizes";
import en from "../util/locales/en";
import ar from "../util/locales/ar";
import ModelsCard from "../Components/Cars/ModelsCard";
import LoadingGif from "../Components/assets/LoadingGif";
import { FilterIcon } from "../Components/assets/Icons";
import noResult from "../images/no-results.png";
import noResult_AR from "../images/no-results-ar.png";
import { useCarFilters } from "../hooks/car/useCarFilters";
import { useBrands } from "../hooks/car/useBrands";
import { useCarData } from "../hooks/car/useCarData";
import SidebarFilter from "../Components/CarsFeatures/SidebarFilter";

const CarPage = () => {
  const language = useSelector((state) => state.language);
  const t = language === "en" ? en : ar;
  const location = useLocation();

  // Mobile state
  const checkIsMobile = () => window.innerWidth <= 768;
  const [isMobile, setIsMobile] = useState(checkIsMobile());
  const [showFilter, setShowFilter] = useState(false);

  // Location state tracking
  const [isLocationStateProcessed, setIsLocationStateProcessed] = useState(false);
  const isInitialMount = useRef(true);

  // Custom hooks
  const {
    brands,
    brandsCount,
    collapseBrands,
    setCollapseBrands,
    searchKey,
    setSearchKey,
    getAllBrands,
    sortBrands,
    searchBrand,
    brandLoading,
  } = useBrands();

  const {
    selectedBrands,
    setSelectedBrands,
    selectedEngineCapacities,
    setSelectedEngineCapacities,
    selectedFuelTypes,
    setSelectedFuelTypes,
    selectedShapes,
    setSelectedShapes,
    selectedTransmissions,
    setSelectedTransmissions,
    selectedModels,
    setSelectedModels,
    maxPrice,
    setMaxPrice,
    minPrice,
    setMinPrice,
    maxYear,
    setMaxYear,
    minYear,
    setMinYear,
    toggleTransmissionCheckbox,
    toggleShapesCheckbox,
    toggleFuelTypesCheckbox,
    toggleEngineCapacitiesCheckbox,
    toggleModelCheckbox,
    clearFilters,
    isCleared,
  } = useCarFilters();

  const { groupedCars, loading, handleFilter } = useCarData({
    selectedBrands,
    selectedEngineCapacities,
    selectedFuelTypes,
    selectedShapes,
    selectedTransmissions,
    selectedModels,
    maxPrice,
    minPrice,
    maxYear,
    minYear,
  });

  const noResult_img = language === "en" ? noResult : noResult_AR;

  // Clear filters when location state changes (new navigation)
  useEffect(() => {
    setIsLocationStateProcessed(false);
    // Reset initial mount flag when location changes
    isInitialMount.current = true;
  }, [location.key]); // location.key changes on each navigation

  // Handle window resize
  useEffect(() => {
    const onResize = () => {
      setIsMobile(checkIsMobile());
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  // Process location state when brands are loaded or immediately if no brand filtering needed
  useEffect(() => {
    if (isLocationStateProcessed) return;

    // Check if we need to wait for brands or can process immediately
    const needsBrandData = location.state?.brand;
    const brandsReady = brands.length > 0 && !brandLoading;
    
    // Process if we don't need brand data OR if brands are ready
    if (!needsBrandData || brandsReady) {
      console.log("Processing location state:", location.state);
      
      if (location.state) {
        // Process brand (ID or name)
        if (location.state.brand && brandsReady) {
          const selectedBrand = brands.find(
            (brand) => 
              brand.id === location.state.brand || 
              brand.id === parseInt(location.state.brand) ||
              brand.name === location.state.brand
          );
          
          if (selectedBrand) {
            console.log("Found brand:", selectedBrand);
            setSelectedBrands(new Set([selectedBrand]));
          } else {
            console.warn("Brand not found:", location.state.brand);
          }
        }

        // Process model (can be ID or name)
        if (location.state.model) {
          // If model is provided, we might need to handle it differently
          // This depends on your model data structure
          console.log("Model from location state:", location.state.model);
          // You might need to fetch models for the brand first or handle differently
        }

        // Process shape/type (can be string or ID)
        if (location.state.type) {
          setSelectedShapes(new Set([location.state.type]));
        }

        // Process engine capacity
        if (location.state.engineCapacity) {
          setSelectedEngineCapacities(new Set([location.state.engineCapacity]));
        }

        // Process fuel type
        if (location.state.fuelType) {
          setSelectedFuelTypes(new Set([location.state.fuelType]));
        }

        // Process transmission
        if (location.state.transmission) {
          setSelectedTransmissions(new Set([location.state.transmission]));
        }

        // Process price range
        if (location.state.maxPrice !== null && location.state.maxPrice !== undefined) {
          setMaxPrice(location.state.maxPrice.toString());
        }
        if (location.state.minPrice !== null && location.state.minPrice !== undefined) {
          setMinPrice(location.state.minPrice.toString());
        }

        // Process year range
        if (location.state.maxYear !== null && location.state.maxYear !== undefined) {
          setMaxYear(location.state.maxYear.toString());
        }
        if (location.state.minYear !== null && location.state.minYear !== undefined) {
          setMinYear(location.state.minYear.toString());
        }
      }

      setIsLocationStateProcessed(true);
      
      // Trigger filter after setting all location state
      setTimeout(() => {
        handleFilter();
      }, 100);
    }
  }, [brands, location.state, brandLoading, isLocationStateProcessed]);

  // Handle filter changes (but skip initial mount and location state processing)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Don't trigger filter during location state processing
    if (!isLocationStateProcessed) {
      return;
    }

    console.log("Filters changed, triggering handleFilter:", {
      selectedBrands: [...selectedBrands].map((b) => ({ id: b.id, name: b.name })),
      selectedModels: [...selectedModels].map((m) => ({
        id: m.id,
        name: m.name,
      })),
      selectedShapes: [...selectedShapes],
      selectedEngineCapacities: [...selectedEngineCapacities],
      selectedFuelTypes: [...selectedFuelTypes],
      selectedTransmissions: [...selectedTransmissions],
    });

    handleFilter();
  }, [
    selectedBrands,
    selectedShapes,
    selectedEngineCapacities,
    selectedFuelTypes,
    selectedTransmissions,
    selectedModels,
    isLocationStateProcessed,
  ]);

  // Enhanced debounced filter for price and year changes
  const debouncedFilterRef = useRef(null);
  
  const triggerDebouncedFilter = () => {
    if (debouncedFilterRef.current) {
      clearTimeout(debouncedFilterRef.current);
    }
    debouncedFilterRef.current = setTimeout(() => {
      handleFilter();
    }, 1000);
  };

  // Handle price filter with debounce
  useEffect(() => {
    if (!isLocationStateProcessed) return;

    // Trigger filter when both prices are set or both are empty
    if ((maxPrice && minPrice) || (maxPrice === "" && minPrice === "")) {
      triggerDebouncedFilter();
    }
    // Also trigger when only one price is set/cleared
    else if (maxPrice !== "" || minPrice !== "") {
      triggerDebouncedFilter();
    }

    return () => {
      if (debouncedFilterRef.current) {
        clearTimeout(debouncedFilterRef.current);
      }
    };
  }, [minPrice, maxPrice, isLocationStateProcessed]);

  // Handle year filter with debounce
  useEffect(() => {
    if (!isLocationStateProcessed) return;

    // Trigger filter when both years are set or both are empty
    if ((maxYear && minYear) || (maxYear === "" && minYear === "")) {
      triggerDebouncedFilter();
    }
    // Also trigger when only one year is set/cleared
    else if (maxYear !== "" || minYear !== "") {
      triggerDebouncedFilter();
    }

    return () => {
      if (debouncedFilterRef.current) {
        clearTimeout(debouncedFilterRef.current);
      }
    };
  }, [minYear, maxYear, isLocationStateProcessed]);

  // Handle search with debounce
  useEffect(() => {
    if (searchKey !== undefined) {
      const delayDebounceFn = setTimeout(() => {
        searchBrand(searchKey);
      }, 1000);
      return () => clearTimeout(delayDebounceFn);
    }
  }, [searchKey]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debouncedFilterRef.current) {
        clearTimeout(debouncedFilterRef.current);
      }
    };
  }, []);

  // Debug logs with more comprehensive information
  console.log("Current state:", {
    isLocationStateProcessed,
    locationState: location.state,
    selectedBrands: [...selectedBrands].map(b => ({ id: b.id, name: b.name })),
    selectedShapes: [...selectedShapes],
    selectedModels: [...selectedModels].map(m => ({ id: m.id, name: m.name })),
    selectedEngineCapacities: [...selectedEngineCapacities],
    selectedFuelTypes: [...selectedFuelTypes],
    selectedTransmissions: [...selectedTransmissions],
    priceRange: { min: minPrice, max: maxPrice },
    yearRange: { min: minYear, max: maxYear },
    brandsLoaded: brands.length > 0,
    brandLoading,
    carsCount: groupedCars.length,
    isLoading: loading,
  });

  return (
    <PageContainer language={language}>
      <Header className="desktop-only">
        <HeaderContainer>
          <TitleContainer>
            <h1>{t.findYourNext}</h1>
            <h1>{t.awesomeCar}</h1>
          </TitleContainer>
          <img src="hidden-car.png" alt="car-header" />
        </HeaderContainer>
      </Header>

      <StyledPage language={language}>
        <ContentContainer>
          <FilterAndSearch className="mobile-only">
            <FilterButton
              onClick={() => {
                setShowFilter(!showFilter);
              }}
            >
              <FilterIcon />
              {t.carsFilter.filter}
            </FilterButton>
          </FilterAndSearch>

          {(!isMobile || showFilter) && (
            <div className="sidebar-container">
              <SidebarFilter
                brands={brands}
                brandLoading={brandLoading}
                selectedBrands={selectedBrands}
                setSelectedBrands={setSelectedBrands}
                selectedEngineCapacities={selectedEngineCapacities}
                setSelectedEngineCapacities={setSelectedEngineCapacities}
                selectedFuelTypes={selectedFuelTypes}
                setSelectedFuelTypes={setSelectedFuelTypes}
                selectedTransmissions={selectedTransmissions}
                setSelectedTransmissions={setSelectedTransmissions}
                selectedShapes={selectedShapes}
                setSelectedShapes={setSelectedShapes}
                selectedModels={selectedModels}
                setSelectedModels={setSelectedModels}
                maxPrice={maxPrice}
                setMaxPrice={setMaxPrice}
                minPrice={minPrice}
                setMinPrice={setMinPrice}
                maxYear={maxYear}
                setMaxYear={setMaxYear}
                minYear={minYear}
                setMinYear={setMinYear}
                handleFilter={handleFilter}
                getAllBrands={getAllBrands}
                brandsCount={brandsCount}
                isMobile={isMobile}
                setCollapseBrands={setCollapseBrands}
                collapseBrands={collapseBrands}
                searchKey={searchKey}
                setSearchKey={setSearchKey}
                sortBrands={sortBrands}
                toggleTransmissionCheckbox={toggleTransmissionCheckbox}
                toggleShapesCheckbox={toggleShapesCheckbox}
                toggleFuelTypesCheckbox={toggleFuelTypesCheckbox}
                toggleEngineCapacitiesCheckbox={toggleEngineCapacitiesCheckbox}
                toggleModelCheckbox={toggleModelCheckbox}
                clearFilters={clearFilters}
                isCleared={isCleared}
              />
            </div>
          )}

          {(!showFilter || !isMobile) &&
            (!loading ? (
              groupedCars.length > 0 ? (
                <div className="cars-list">
                  {groupedCars.map((group) => (
                    <div className="brand" key={group.brand_name}>
                      <div className="brand-title">
                        {language === "en"
                          ? group.brand_name
                          : group.brand_name_ar}
                      </div>
                      <div className="models">
                        {group.models.map((model) => (
                          <ModelsCard key={model.model_name} model={model} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-result">
                  <img src={noResult_img} alt="no cars to show" />
                </div>
              )
            ) : (
              <div className="loading-div">
                <LoadingGif />
              </div>
            ))}
        </ContentContainer>
      </StyledPage>
    </PageContainer>
  );
};


// Styled Components

const Header = styled.div`
  height: calc(300 / 1440 * 100vw);
  background-image: url("car-header.png");
  background-size: cover;
  background-position: center;

  padding: 0 5vw;
`;

const HeaderContainer = styled.div`
  display: flex;
  width: calc(1248 / 1440 * 100vw);
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;

  img {
    margin-top: 1vw;
    width: calc(400 / 1440 * 100vw);
    height: calc(200 / 1440 * 100vw);
  }
`;

const TitleContainer = styled.div`
  h1 {
    font-weight: 700;
    font-size: 2.5vw;
    line-height: 3.5vw;
    color: #ffffff;
  }
`;

const PageContainer = styled.div`
  position: relative;
  direction: ${(props) => (props.language === "en" ? "ltr" : "rtl")};
  * {
    font-family: ${(props) =>
      props.language === "en" ? "Poppins" : "Almarai"};
  }
`;

const FilterAndSearch = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.667vw;
  margin-top: calc(20 / 375 * 100vw);
  padding: 0 calc(16 / 375 * 100vw);
  width: 100%;
`;

const FilterButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.833vw;
  height: calc(62 / 375 * 100vw);
  padding: calc(17 / 375 * 100vw);
  background: white;
  border-radius: calc(4 / 375 * 100vw);
  border: calc(1 / 375 * 100vw) solid #e4e6e8;
`;

const ContentContainer = styled.div`
  width: calc(1248 / 1440 * 100vw);
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  gap: 4vw;
  padding: 0 0 1.944vw 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
    width: 100%;
  }
`;

const StyledPage = styled.div`
  background-color: #ffffff;
  width: 100%;
  .cars-list {
    display: flex;
    flex-direction: column;
    gap: 1.667vw;
    .brand {
      display: flex;
      flex-direction: column;
      gap: 1.667vw;
      .brand-title {
        width: 57.6vw;
        height: 4.167vw;
        padding: 0.972vw 1.667vw;
        background: rgba(21, 76, 214, 0.05);
        border: 0.069vw solid #e4e6e8;
        border-radius: 0.278vw;
        font-family: ${(props) =>
          props.language == "en" ? "Inter" : "Almarai"};
        font-weight: 700;
        font-size: 1.528vw;
        line-height: 2.222vw;
        color: #0d0a19;

        @media (min-width: 1440px) {
          font-size: 22px;
          line-height: 32px;
        }
      }
      .models {
        display: flex;
        flex-direction: column;
        gap: 1.111vw;
      }
    }
  }
  .no-result {
    width: 51.944vw;
    height: 58.194vw;
    background: #ffffff;
    border: ${desktopSizes._1px} solid #e4e6e8;
    border-radius: ${desktopSizes._4px};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    img {
      width: 28.889vw;
      height: 19.931vw;
    }

    @media (min-width: 1440px) {
      width: 748px;
      height: 838px;
      img {
        width: 416px;
        height: 287px;
      }
    }
  }
  .loading-div {
    display: flex;
    justify-content: center;
    width: 51.875vw;
    @media (max-width: 768px) {
      width: 100vw;
    }
  }

  @media (max-width: 768px) {
    /* min-height: 100vh;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0;

    .filter-btn-container {
      width: 99.467vw;
      height: 14.933vw;
      background: #f1f1f1;
      margin: 1vw;
      padding: ${mobileSizes._16px};

      button {
        cursor: pointer;
        background: none;
        border: none;
        outline: none;
        display: flex;
        align-items: center;
        gap: ${mobileSizes._8px};

        svg {
          width: ${mobileSizes._20px};
          height: ${mobileSizes._20px};
        }
        p {
          font-family: ${(props) =>
      props.language == "en" ? "Poppins" : "Almarai"};
          font-style: normal;
          font-weight: 600;
          font-size: ${mobileSizes._16px};
          line-height: ${mobileSizes._24px};
          color: #0d0a19;
        }
      }
    } */
    .sidebar-container {
      padding: ${mobileSizes._16px} ${mobileSizes._16px} ${mobileSizes._32px}
        ${mobileSizes._16px};
    }
    .cars-list {
      display: flex;
      flex-direction: column;
      gap: ${mobileSizes._16px};
      margin-top: ${mobileSizes._16px};
      padding: calc(16 / 375 * 100vw);
      .brand {
        display: flex;
        flex-direction: column;
        gap: ${mobileSizes._16px};
        .brand-title {
          width: 91.467vw;
          height: 13.6vw;
          padding: ${mobileSizes._16px};

          border: ${mobileSizes._1px} solid #e4e6e8;
          border-radius: ${mobileSizes._4px};

          font-style: normal;
          font-weight: 700;
          font-size: ${mobileSizes._16px};
          line-height: ${mobileSizes._19px};
        }
        .models {
          display: flex;
          flex-direction: column;
          gap: ${mobileSizes._16px};
        }
      }
    }
    .no-result {
      flex: 1;
      width: 100vw;
      height: auto;
      background: none;
      border: none;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      img {
        width: 70.4vw;
        height: 52.533vw;
      }
    }
  }
`;

export default CarPage;
